import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Flame, Crosshair, Hand, Crown, Search, BarChart3 } from "lucide-react";
import api from "@/lib/api";

const EASE = [0.22, 1, 0.36, 1];
const SEASONS = ["all", "5", "4", "3", "2", "1"];
const SEASON_LABEL = { all: "All-Time", "5": "ASL 5.0", "4": "ASL 4.0", "3": "ASL 3.0", "2": "ASL 2.0", "1": "ASL 1.0" };

const CATEGORIES = [
  { key: "batting", label: "Batting", icon: Flame, stat: (r) => `${r.runs} runs` },
  { key: "bowling", label: "Bowling", icon: Crosshair, stat: (r) => `${r.wickets} wkts` },
  { key: "fielding", label: "Fielding", icon: Hand, stat: (r) => `${r.dismissals} dismissals` },
  { key: "mvp", label: "MVP", icon: Crown, stat: (r) => `${r.total} pts` },
];

const COLUMNS = {
  batting: [
    ["mat", "Mat"], ["inns", "Inns"], ["runs", "Runs"], ["highest", "HS"], ["avg", "Avg"], ["sr", "SR"],
    ["fours", "4s"], ["sixes", "6s"], ["fifties", "50s"], ["hundreds", "100s"],
  ],
  bowling: [
    ["mat", "Mat"], ["overs", "Overs"], ["runs", "Runs"], ["wickets", "Wkts"], ["best", "Best"], ["maidens", "Mdns"],
    ["avg", "Avg"], ["econ", "Econ"], ["sr", "SR"],
  ],
  fielding: [
    ["mat", "Mat"], ["dismissals", "Dis"], ["catches", "Ct"], ["caught_and_bowl", "C&B"], ["caught_behind", "CB"],
    ["run_outs", "RO"], ["assist_run_outs", "ARO"], ["stumpings", "St"],
  ],
  mvp: [
    ["mat", "Mat"], ["total", "Points"], ["batting", "Bat"], ["bowling", "Bowl"], ["fielding", "Field"],
  ],
};

const KEY_COL = { batting: "runs", bowling: "wickets", fielding: "dismissals", mvp: "total" };
const PODIUM_STYLE = [
  { ring: "border-amber-400/60", chip: "bg-amber-400 text-league-ink", label: "1st" },
  { ring: "border-white/25", chip: "bg-white/80 text-league-ink", label: "2nd" },
  { ring: "border-orange-700/50", chip: "bg-orange-700/80 text-white", label: "3rd" },
];

const fmt = (v) => (v === null || v === undefined ? "—" : v);

export default function Stats() {
  const [season, setSeason] = useState("all");
  const [category, setCategory] = useState("batting");
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/stats/boards?season=${season}&category=${category}`)
      .then((r) => setBoard(r.data))
      .catch(() => setBoard(null))
      .finally(() => setLoading(false));
  }, [season, category]);

  const rows = useMemo(() => {
    if (!board) return [];
    const q = query.trim().toLowerCase();
    if (!q) return board.rows;
    return board.rows.filter((r) => r.player.toLowerCase().includes(q) || (r.team || "").toLowerCase().includes(q));
  }, [board, query]);

  const cat = CATEGORIES.find((c) => c.key === category);
  const podium = !query && board ? board.rows.slice(0, 3) : [];
  const cols = COLUMNS[category];

  return (
    <main className="pt-28 pb-24 bg-league-ink min-h-screen text-white" data-testid="stats-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <span className="text-[11px] font-bold uppercase tracking-[0.3em] text-league-red flex items-center gap-2">
          <BarChart3 size={14} /> Official CricHeroes Records
        </span>
        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-3 mb-4">
          THE <span className="text-outline-white">STAT BOOK</span>
        </h1>
        <p className="text-white/40 max-w-xl mb-10 leading-relaxed text-sm">
          Every run, wicket, catch and MVP point from Seasons 1.0 to 5.0 — plus the all-time leaderboard across the league's history.
        </p>

        <div className="flex flex-wrap gap-2 mb-4" data-testid="stats-season-filters">
          {SEASONS.map((s) => (
            <button
              key={s}
              onClick={() => setSeason(s)}
              data-testid={`stats-season-${s}`}
              className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                season === s ? "bg-league-red text-white" : "bg-white/5 text-white/60 border border-white/10 hover:border-league-red hover:text-white"
              }`}
            >
              {SEASON_LABEL[s]}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-10" data-testid="stats-category-tabs">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`stats-tab-${c.key}`}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors duration-200 ${
                category === c.key ? "bg-league-blue text-white" : "bg-white/5 text-white/60 border border-white/10 hover:border-league-blue hover:text-white"
              }`}
            >
              <c.icon size={14} /> {c.label}
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-64 mt-3 sm:mt-0">
            <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search player or team…"
              aria-label="Search player or team"
              data-testid="stats-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-white/10 bg-white/5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-league-blue"
            />
          </div>
        </div>

        {loading && <p className="text-white/40 py-16 text-center text-sm uppercase tracking-widest" data-testid="stats-loading">Loading leaderboard…</p>}
        {!loading && !board && <p className="text-white/40 py-16 text-center" data-testid="stats-empty">No stats available.</p>}

        {!loading && board && (
          <>
            {podium.length === 3 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12" data-testid="stats-podium">
                {podium.map((r, i) => (
                  <motion.div
                    key={`${r.player}-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
                    className={`rounded-2xl border ${PODIUM_STYLE[i].ring} bg-white/5 backdrop-blur p-6 ${i === 0 ? "sm:-translate-y-2" : ""}`}
                    data-testid={`stats-podium-${i + 1}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${PODIUM_STYLE[i].chip}`}>{PODIUM_STYLE[i].label}</span>
                      <cat.icon size={18} className="text-white/30" />
                    </div>
                    <p className="font-oswald text-2xl font-semibold leading-tight">{r.player}</p>
                    <p className="text-xs text-white/40 mt-1 uppercase tracking-widest">{r.team || `${r.seasons} seasons`}</p>
                    <p className="font-display text-4xl mt-4 text-league-red">{fmt(r[KEY_COL[category]])}</p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40 mt-1">{cat.stat(r)}</p>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid="stats-table-wrap">
              <table className="w-full text-sm min-w-[720px]" data-testid="stats-table">
                <thead>
                  <tr className="bg-white/5 text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 text-left">
                    <th className="px-4 py-3 w-12">#</th>
                    <th className="px-4 py-3">Player</th>
                    {season !== "all" && <th className="px-4 py-3">Team</th>}
                    {season === "all" && <th className="px-4 py-3">Seasons</th>}
                    {cols.map(([k, label]) => (
                      <th key={k} className={`px-3 py-3 text-right ${k === KEY_COL[category] ? "text-league-red" : ""}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={`${r.rank}-${r.player}`}
                      className="border-t border-white/5 hover:bg-white/5 transition-colors duration-150"
                      data-testid={`stats-row-${r.rank}`}
                    >
                      <td className={`px-4 py-3 font-oswald font-semibold ${r.rank <= 3 ? "text-league-red" : "text-white/40"}`}>{r.rank}</td>
                      <td className="px-4 py-3 font-oswald font-semibold text-white">{r.player}</td>
                      {season !== "all" && <td className="px-4 py-3 text-white/50 text-xs">{r.team}</td>}
                      {season === "all" && <td className="px-4 py-3 text-white/50 text-xs">{r.seasons}</td>}
                      {cols.map(([k]) => (
                        <td key={k} className={`px-3 py-3 text-right font-oswald ${k === KEY_COL[category] ? "text-white font-semibold" : "text-white/60"}`}>
                          {fmt(r[k])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && (
                <p className="text-white/40 py-10 text-center text-sm" data-testid="stats-no-match">No players match that search.</p>
              )}
            </div>
            <p className="text-white/30 text-[11px] uppercase tracking-widest mt-4" data-testid="stats-count">
              {rows.length} players • {SEASON_LABEL[season]} • {cat.label}
            </p>
          </>
        )}
      </div>
    </main>
  );
}
